import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Calendar, Lock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const toLocalDT = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
};
const toISO = (local) => local ? new Date(local).toISOString() : null;

/**
 * Модалка «Сдвинуть сроки» для опубликованного тендера.
 *
 * Логика разрешений совпадает с backend:
 *   • phase = waiting (+ collecting без заявок): все 4 поля
 *   • phase = collecting/waiting_bids c заявками: app_end + bid_start + bid_end
 *   • phase = bidding: только bid_end
 */
export default function ShiftDatesDialog({ tender, open, onClose, onSaved }) {
  const [appStart, setAppStart] = useState('');
  const [appEnd, setAppEnd] = useState('');
  const [bidStart, setBidStart] = useState('');
  const [bidEnd, setBidEnd] = useState('');
  const [hasApps, setHasApps] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !tender) return;
    setAppStart(toLocalDT(tender.app_start));
    setAppEnd(toLocalDT(tender.app_end));
    setBidStart(toLocalDT(tender.bid_start));
    setBidEnd(toLocalDT(tender.bid_end));
    // Подтянем сведения о заявках, чтобы знать пермишн на app_start.
    api.get(`/tenders/${tender.id}/applications`)
      .then(({ data }) => setHasApps(Array.isArray(data) && data.length > 0))
      .catch(() => setHasApps(false));
  }, [open, tender]);

  const phase = tender?.phase;
  const allowed = useMemo(() => {
    if (phase === 'bidding') return new Set(['bid_end']);
    if ((phase === 'collecting' || phase === 'waiting_bids') && hasApps)
      return new Set(['app_end', 'bid_start', 'bid_end']);
    return new Set(['app_start', 'app_end', 'bid_start', 'bid_end']);
  }, [phase, hasApps]);

  const hint = useMemo(() => {
    if (phase === 'bidding') return 'Идут торги — доступно только время окончания торгов.';
    if ((phase === 'collecting' || phase === 'waiting_bids') && hasApps)
      return 'Есть поданные заявки — нельзя сдвинуть начало приёма. Доступны: окончание заявок, начало и окончание торгов.';
    if (phase === 'completed' || phase === 'cancelled') return 'Тендер завершён — сроки нельзя менять.';
    return 'Заявок ещё нет — все 4 даты доступны для редактирования.';
  }, [phase, hasApps]);

  const submit = async () => {
    const body = {};
    if (allowed.has('app_start') && appStart) body.app_start = toISO(appStart);
    if (allowed.has('app_end') && appEnd) body.app_end = toISO(appEnd);
    if (allowed.has('bid_start') && bidStart) body.bid_start = toISO(bidStart);
    if (allowed.has('bid_end') && bidEnd) body.bid_end = toISO(bidEnd);
    if (Object.keys(body).length === 0) {
      toast.error('Нечего менять'); return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/tenders/${tender.id}/dates`, body);
      toast.success('Сроки обновлены');
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Не удалось обновить сроки');
    } finally {
      setBusy(false);
    }
  };

  const Field = ({ field, label, value, set }) => {
    const isAllowed = allowed.has(field);
    return (
      <div className="space-y-1">
        <Label className="text-xs text-slate-600 flex items-center gap-1.5">
          {isAllowed ? <Calendar className="w-3 h-3" /> : <Lock className="w-3 h-3 text-slate-400" />}
          {label}
          {!isAllowed && <span className="text-[10px] text-slate-400">(заблокировано)</span>}
        </Label>
        <Input type="datetime-local" value={value} onChange={(e) => set(e.target.value)}
          disabled={!isAllowed}
          data-testid={`shift-dates-${field}`}
          className={`h-8 text-xs rounded-sm border-slate-300 ${!isAllowed ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : ''}`} />
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="shift-dates-dialog">
        <DialogHeader>
          <DialogTitle>Сдвинуть сроки тендера</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-700 flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-sm p-2">
          <AlertCircle className="w-3.5 h-3.5 text-blue-700 mt-0.5 shrink-0" />
          <span>{hint}</span>
        </div>
        <div className="space-y-3 mt-2">
          <Field field="app_start" label="Заявки — начало" value={appStart} set={setAppStart} />
          <Field field="app_end" label="Заявки — окончание" value={appEnd} set={setAppEnd} />
          <Field field="bid_start" label="Торги — начало" value={bidStart} set={setBidStart} />
          <Field field="bid_end" label="Торги — окончание" value={bidEnd} set={setBidEnd} />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Отмена</Button>
          <Button onClick={submit} disabled={busy}
            data-testid="shift-dates-save"
            className="bg-[#0046AD] hover:bg-[#003A8C]">
            {busy ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
